import type {
	WorkflowDbFieldSchema,
	WorkflowDbFieldType,
	WorkflowDbRecord,
	WorkflowDbSchema,
	WorkflowDbTableData,
} from "./workflow-db.types";

const OBJECT_ROOT_PATH = "$";

export const createEmptyWorkflowDbSchema = (): WorkflowDbSchema => ({
	type: "object",
	properties: [],
});

type ServerSchemaNode = {
	type?: string;
	description?: string;
	default?: unknown;
	nullable?: boolean;
	pattern?: string;
	enumRef?: string;
	key?: boolean;
	properties?: Record<string, ServerSchemaNode>;
	items?: ServerSchemaNode;
};

type ServerEnums = Record<string, unknown[]>;

const convertServerNode = (
	key: string,
	node: ServerSchemaNode,
	enums: ServerEnums,
): WorkflowDbFieldSchema => {
	const hasEnumRef = Boolean(node.enumRef);
	const fieldType: WorkflowDbFieldType = hasEnumRef ? "enum" : (node.type as WorkflowDbFieldType) ?? "string";

	const field: WorkflowDbFieldSchema = {
		key,
		type: fieldType,
		required: node.key === true,
	};

	if (hasEnumRef && node.enumRef && enums[node.enumRef]) {
		field.enumOptions = enums[node.enumRef].map(String);
	}

	if (node.type === "object" && node.properties) {
		field.properties = Object.entries(node.properties).map(([k, v]) =>
			convertServerNode(k, v, enums),
		);
	}

	if (node.type === "array" && node.items) {
		if (node.items.type === "object" && node.items.properties) {
			field.arrayItemProperties = Object.entries(node.items.properties).map(([k, v]) =>
				convertServerNode(k, v, enums),
			);
		} else {
			field.arrayItemType = (node.items.type as WorkflowDbFieldSchema["arrayItemType"]) ?? "string";
		}
	}

	return field;
};

export const normalizeWorkflowDbSchema = (
	value: unknown,
	enums?: Record<string, unknown[]>,
): WorkflowDbSchema => {
	if (!value || typeof value !== "object") {
		return createEmptyWorkflowDbSchema();
	}

	// 기존 프론트엔드 포맷 호환 (이미 변환된 스키마)
	const candidate = value as Partial<WorkflowDbSchema>;
	if (candidate.type === "object" && Array.isArray(candidate.properties)) {
		return { type: "object", properties: candidate.properties };
	}

	// 서버 포맷 변환: Record<string, SchemaNode> → { type: "object", properties: [...] }
	const serverSchema = value as Record<string, ServerSchemaNode>;
	const resolvedEnums: ServerEnums = enums ?? {};

	const properties: WorkflowDbFieldSchema[] = Object.entries(serverSchema).map(
		([key, node]) => convertServerNode(key, node, resolvedEnums),
	);

	return { type: "object", properties };
};

export const normalizeWorkflowDbRecords = (value: unknown): WorkflowDbRecord[] => {
	if (Array.isArray(value)) {
		return value.filter((item): item is WorkflowDbRecord =>
			Boolean(item && typeof item === "object"),
		);
	}

	if (value && typeof value === "object") {
		const candidate = value as { items?: unknown };
		if (Array.isArray(candidate.items)) {
			return candidate.items.filter((item): item is WorkflowDbRecord =>
				Boolean(item && typeof item === "object"),
			);
		}
	}

	return [];
};

export const normalizeWorkflowDbTableData = (
	value: unknown,
	tableNames: string[] = [],
): WorkflowDbTableData => {
	const next: WorkflowDbTableData = {};

	for (const tableName of tableNames) {
		next[tableName] = [];
	}

	if (!value || typeof value !== "object") {
		return next;
	}

	if (Array.isArray(value)) {
		const firstTable = tableNames[0];
		if (firstTable) {
			next[firstTable] = normalizeWorkflowDbRecords(value);
		}
		return next;
	}

	for (const [key, tableValue] of Object.entries(value as Record<string, unknown>)) {
		next[key] = normalizeWorkflowDbRecords(tableValue);
	}

	return next;
};

const createDefaultValueByField = (field: WorkflowDbFieldSchema): unknown => {
	switch (field.type) {
		case "string":
			return "";
		case "number":
			return 0;
		case "boolean":
			return false;
		case "enum":
			return field.enumOptions?.[0] ?? "";
		case "file":
			return null;
		case "array": {
			return [];
		}
		case "object": {
			const nested: Record<string, unknown> = {};
			for (const child of field.properties ?? []) {
				nested[child.key] = createDefaultValueByField(child);
			}
			return nested;
		}
		default:
			return "";
	}
};

export const createEmptyRecordFromSchema = (schema: WorkflowDbSchema): WorkflowDbRecord => {
	const next: WorkflowDbRecord = {};

	for (const field of schema.properties) {
		next[field.key] = createDefaultValueByField(field);
	}

	return next;
};

type ObjectNode = {
	fields: WorkflowDbFieldSchema[];
	nodeType: "root" | "object";
};

const findObjectNodeByPath = (schema: WorkflowDbSchema, path: string): ObjectNode | null => {
	if (path === OBJECT_ROOT_PATH) {
		return { fields: schema.properties, nodeType: "root" };
	}

	const segments = path.split(".").filter(Boolean);
	let fields = schema.properties;

	for (const segment of segments) {
		const target = fields.find((field) => field.key === segment);
		if (!target) {
			return null;
		}

		if (target.type === "object") {
			fields = target.properties ?? [];
			continue;
		}

		if (target.type === "array" && Array.isArray(target.arrayItemProperties)) {
			fields = target.arrayItemProperties ?? [];
			continue;
		}

		return null;
	}

	return { fields, nodeType: "object" };
};

export const listObjectPaths = (schema: WorkflowDbSchema): string[] => {
	const result = [OBJECT_ROOT_PATH];

	const walk = (fields: WorkflowDbFieldSchema[], basePath: string) => {
		for (const field of fields) {
			const path = basePath === OBJECT_ROOT_PATH ? field.key : `${basePath}.${field.key}`;

			if (field.type === "object") {
				result.push(path);
				walk(field.properties ?? [], path);
			}

			if (field.type === "array" && Array.isArray(field.arrayItemProperties)) {
				const arrayObjectPath = `${path}[]`;
				result.push(arrayObjectPath);
				walk(field.arrayItemProperties ?? [], arrayObjectPath);
			}
		}
	};

	walk(schema.properties, OBJECT_ROOT_PATH);

	return result;
};

export const getFieldsAtPath = (
	schema: WorkflowDbSchema,
	path: string,
): WorkflowDbFieldSchema[] => {
	const normalizedPath = path.endsWith("[]") ? path.slice(0, -2) : path;
	const objectNode = findObjectNodeByPath(schema, normalizedPath);

	if (!objectNode) {
		return [];
	}

	if (path.endsWith("[]")) {
		const segments = normalizedPath.split(".").filter(Boolean);
		const targetFieldKey = segments.at(-1);
		if (!targetFieldKey) {
			return [];
		}

		const parentPath = segments.slice(0, -1).join(".");
		const parentNode = findObjectNodeByPath(schema, parentPath || OBJECT_ROOT_PATH);
		const targetField = parentNode?.fields.find((field) => field.key === targetFieldKey);

		if (targetField?.type === "array" && Array.isArray(targetField.arrayItemProperties)) {
			return targetField.arrayItemProperties ?? [];
		}

		return [];
	}

	return objectNode.fields;
};

const cloneField = (field: WorkflowDbFieldSchema): WorkflowDbFieldSchema => ({
	...field,
	enumOptions: field.enumOptions ? [...field.enumOptions] : undefined,
	properties: field.properties ? field.properties.map(cloneField) : undefined,
	arrayItemProperties: field.arrayItemProperties
		? field.arrayItemProperties.map(cloneField)
		: undefined,
});

const cloneSchema = (schema: WorkflowDbSchema): WorkflowDbSchema => ({
	type: "object",
	properties: schema.properties.map(cloneField),
});

export const addFieldAtPath = (
	schema: WorkflowDbSchema,
	path: string,
	field: WorkflowDbFieldSchema,
): WorkflowDbSchema => {
	const next = cloneSchema(schema);

	if (path === OBJECT_ROOT_PATH) {
		next.properties.push(field);
		return next;
	}

	const normalizedPath = path.endsWith("[]") ? path.slice(0, -2) : path;
	const segments = normalizedPath.split(".").filter(Boolean);

	let currentFields = next.properties;

	for (let i = 0; i < segments.length; i++) {
		const key = segments[i];
		const target = currentFields.find((item) => item.key === key);

		if (!target) {
			return next;
		}

		const isLast = i === segments.length - 1;

		if (
			isLast &&
			path.endsWith("[]") &&
			target.type === "array" &&
			Array.isArray(target.arrayItemProperties)
		) {
			target.arrayItemProperties = target.arrayItemProperties ?? [];
			target.arrayItemProperties.push(field);
			return next;
		}

		if (target.type === "object") {
			target.properties = target.properties ?? [];
			currentFields = target.properties;
			continue;
		}

		if (target.type === "array" && Array.isArray(target.arrayItemProperties)) {
			target.arrayItemProperties = target.arrayItemProperties ?? [];
			currentFields = target.arrayItemProperties;
			continue;
		}

		return next;
	}

	currentFields.push(field);

	return next;
};

export const updateFieldAtPath = (
	schema: WorkflowDbSchema,
	path: string,
	originalKey: string,
	nextField: WorkflowDbFieldSchema,
): WorkflowDbSchema => {
	const next = cloneSchema(schema);

	if (path === OBJECT_ROOT_PATH) {
		next.properties = next.properties.map((field) =>
			field.key === originalKey ? nextField : field,
		);
		return next;
	}

	const normalizedPath = path.endsWith("[]") ? path.slice(0, -2) : path;
	const segments = normalizedPath.split(".").filter(Boolean);

	let currentFields = next.properties;

	for (let i = 0; i < segments.length; i++) {
		const key = segments[i];
		const target = currentFields.find((field) => field.key === key);

		if (!target) {
			return next;
		}

		const isLast = i === segments.length - 1;

		if (
			isLast &&
			path.endsWith("[]") &&
			target.type === "array" &&
			Array.isArray(target.arrayItemProperties)
		) {
			target.arrayItemProperties = (target.arrayItemProperties ?? []).map((field) =>
				field.key === originalKey ? nextField : field,
			);
			return next;
		}

		if (target.type === "object") {
			if (isLast) {
				target.properties = (target.properties ?? []).map((field) =>
					field.key === originalKey ? nextField : field,
				);
				return next;
			}

			target.properties = target.properties ?? [];
			currentFields = target.properties;
			continue;
		}

		if (target.type === "array" && Array.isArray(target.arrayItemProperties)) {
			if (isLast) {
				target.arrayItemProperties = (target.arrayItemProperties ?? []).map((field) =>
					field.key === originalKey ? nextField : field,
				);
				return next;
			}

			target.arrayItemProperties = target.arrayItemProperties ?? [];
			currentFields = target.arrayItemProperties;
			continue;
		}

		return next;
	}

	next.properties = next.properties.map((field) => (field.key === originalKey ? nextField : field));

	return next;
};

export const removeFieldAtPath = (
	schema: WorkflowDbSchema,
	path: string,
	fieldKey: string,
): WorkflowDbSchema => {
	const next = cloneSchema(schema);

	if (path === OBJECT_ROOT_PATH) {
		next.properties = next.properties.filter((field) => field.key !== fieldKey);
		return next;
	}

	const normalizedPath = path.endsWith("[]") ? path.slice(0, -2) : path;
	const segments = normalizedPath.split(".").filter(Boolean);
	let currentFields = next.properties;

	for (let i = 0; i < segments.length; i++) {
		const key = segments[i];
		const target = currentFields.find((field) => field.key === key);
		if (!target) {
			return next;
		}

		const isLast = i === segments.length - 1;

		if (
			isLast &&
			path.endsWith("[]") &&
			target.type === "array" &&
			Array.isArray(target.arrayItemProperties)
		) {
			target.arrayItemProperties = (target.arrayItemProperties ?? []).filter(
				(field) => field.key !== fieldKey,
			);
			return next;
		}

		if (target.type === "object") {
			if (isLast) {
				target.properties = (target.properties ?? []).filter((field) => field.key !== fieldKey);
				return next;
			}
			target.properties = target.properties ?? [];
			currentFields = target.properties;
			continue;
		}

		if (target.type === "array" && Array.isArray(target.arrayItemProperties)) {
			if (isLast) {
				target.arrayItemProperties = (target.arrayItemProperties ?? []).filter(
					(field) => field.key !== fieldKey,
				);
				return next;
			}
			target.arrayItemProperties = target.arrayItemProperties ?? [];
			currentFields = target.arrayItemProperties;
			continue;
		}

		return next;
	}

	return next;
};

export const hasDuplicateFieldKey = (fields: WorkflowDbFieldSchema[], fieldKey: string): boolean =>
	fields.some((field) => field.key === fieldKey);
